import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import * as XLSX from "xlsx";
import {
    TextT,
    Image as ImgIcon,
    User as UserIcon,
    PenNib,
    Rectangle,
    QrCode,
    Barcode,
    UploadSimple,
    FloppyDisk,
    ArrowLeft,
    Trash,
    CaretLeft,
    CaretRight,
    FilePdf,
    FileImage,
    Printer,
    Palette,
    Images,
    Sparkle,
} from "@phosphor-icons/react";

const MM_PER_PX = 3.78;
const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_TEXT = (type = "text") => ({
    id: uid(),
    kind: "text",
    x: 20,
    y: 20,
    w: 160,
    h: 30,
    text: type === "placeholder" ? "{{StudentName}}" : "Your Text",
    bind: type === "placeholder" ? "StudentName" : "",
    color: "#0A0A0A",
    bg: "transparent",
    fontSize: 9,
    fontFamily: "Inter, sans-serif",
    fontWeight: "600",
    align: "left",
    rotation: 0,
    z: 2,
});
const DEFAULT_PHOTO = () => ({
    id: uid(),
    kind: "photo",
    x: 210,
    y: 30,
    w: 90,
    h: 110,
    bind: "Photo",
    src: "",
    border: 2,
    borderColor: "#2563EB",
    radius: 0,
    z: 2,
});
const DEFAULT_IMAGE = (src = "") => ({
    id: uid(),
    kind: "image",
    x: 20,
    y: 60,
    w: 120,
    h: 120,
    src,
    radius: 0,
    z: 2,
});
const DEFAULT_SIGNATURE = (src = "") => ({
    id: uid(),
    kind: "signature",
    x: 210,
    y: 150,
    w: 80,
    h: 28,
    src,
    radius: 0,
    opacity: 1,
    blend: "normal",
    z: 3,
});
const DEFAULT_RECT = () => ({
    id: uid(),
    kind: "rect",
    x: 0,
    y: 0,
    w: 324,
    h: 40,
    bg: "#2563EB",
    radius: 0,
    z: 0,
});
const DEFAULT_QR = () => ({
    id: uid(),
    kind: "qr",
    x: 20,
    y: 140,
    w: 50,
    h: 50,
    bind: "RegNo",
    value: "{{RegNo}}",
    color: "#0A0A0A",
    bg: "#FFFFFF",
    z: 3,
});
const DEFAULT_BARCODE = () => ({
    id: uid(),
    kind: "barcode",
    x: 20,
    y: 170,
    w: 120,
    h: 34,
    bind: "RegNo",
    value: "{{RegNo}}",
    format: "CODE128",
    color: "#0A0A0A",
    bg: "#FFFFFF",
    showText: true,
    z: 3,
});

const BG_PRESETS = [
    {
        id: "royal",
        name: "Royal Blue",
        src: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 750"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#1d4ed8"/><stop offset="55%" stop-color="#1e3a8a"/><stop offset="100%" stop-color="#0f172a"/></linearGradient></defs><rect width="1200" height="750" fill="url(#g)"/><circle cx="1030" cy="90" r="180" fill="#60a5fa" fill-opacity="0.24"/><circle cx="140" cy="620" r="200" fill="#38bdf8" fill-opacity="0.22"/></svg>')}`,
    },
    {
        id: "emerald",
        name: "Emerald Grid",
        src: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 750"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#022c22"/><stop offset="55%" stop-color="#065f46"/><stop offset="100%" stop-color="#0f766e"/></linearGradient><pattern id="p" width="70" height="70" patternUnits="userSpaceOnUse"><path d="M70 0H0V70" fill="none" stroke="#99f6e4" stroke-opacity="0.15"/></pattern></defs><rect width="1200" height="750" fill="url(#g)"/><rect width="1200" height="750" fill="url(#p)"/></svg>')}`,
    },
    {
        id: "sunset",
        name: "Sunset Arc",
        src: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 750"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#7c2d12"/><stop offset="45%" stop-color="#dc2626"/><stop offset="100%" stop-color="#f59e0b"/></linearGradient></defs><rect width="1200" height="750" fill="url(#g)"/><path d="M0 600C180 480 330 520 500 440C700 346 760 150 1200 190V750H0Z" fill="#fff" fill-opacity="0.14"/></svg>')}`,
    },
];

// --- QR / Barcode cached renderers --------------------------------------------------
function QRRender({ value, color = "#000", bg = "#fff" }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let cancelled = false;
        QRCode.toDataURL(String(value || "QR"), {
            margin: 0,
            color: { dark: color, light: bg },
            errorCorrectionLevel: "M",
            width: 512,
        })
            .then((url) => {
                if (!cancelled) setSrc(url);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [value, color, bg]);
    return src ? (
        <img
            src={src}
            alt=""
            style={{ width: "100%", height: "100%", display: "block" }}
        />
    ) : null;
}
function BarcodeRender({
    value,
    format = "CODE128",
    color = "#000",
    bg = "#fff",
    showText = true,
}) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        try {
            const canvas = document.createElement("canvas");
            JsBarcode(canvas, String(value || "0000"), {
                format,
                lineColor: color,
                background: bg,
                displayValue: showText,
                margin: 0,
                height: 60,
                fontSize: 14,
            });
            setSrc(canvas.toDataURL("image/png"));
        } catch {
            setSrc("");
        }
    }, [value, format, color, bg, showText]);
    return src ? (
        <img
            src={src}
            alt=""
            style={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "fill",
            }}
        />
    ) : (
        <div
            style={{
                width: "100%",
                height: "100%",
                background: bg,
                color: "#dc2626",
                fontSize: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            invalid value
        </div>
    );
}

// --- Canvas element -----------------------------------------------------------------
function CanvasEl({
    el,
    selected,
    onSelect,
    onChange,
    onDelete,
    scale,
    row,
    photoMap,
    otherElements,
    autoAlign,
    onAlignGuideChange,
    canvasWidth,
    canvasHeight,
}) {
    const startRef = useRef(null);
    const mode = useRef("move");

    const snapMove = useCallback(
        (x, y) => {
            if (!autoAlign) {
                return { x, y, guideX: null, guideY: null };
            }
            const threshold = 6;
            let nextX = x;
            let nextY = y;
            let guideX = null;
            let guideY = null;

            const w = el.w;
            const h = el.h;
            const selfX = [x, x + w / 2, x + w];
            const selfY = [y, y + h / 2, y + h];

            const xTargets = [0, canvasWidth / 2, canvasWidth];
            const yTargets = [0, canvasHeight / 2, canvasHeight];

            (otherElements || []).forEach((o) => {
                xTargets.push(o.x, o.x + o.w / 2, o.x + o.w);
                yTargets.push(o.y, o.y + o.h / 2, o.y + o.h);
            });

            let bestDx = threshold + 1;
            xTargets.forEach((t) => {
                selfX.forEach((s) => {
                    const d = t - s;
                    if (Math.abs(d) < Math.abs(bestDx)) {
                        bestDx = d;
                        guideX = t;
                    }
                });
            });
            if (Math.abs(bestDx) <= threshold) {
                nextX = x + bestDx;
            } else {
                guideX = null;
            }

            const movedY = [nextY, nextY + h / 2, nextY + h];
            let bestDy = threshold + 1;
            yTargets.forEach((t) => {
                movedY.forEach((s) => {
                    const d = t - s;
                    if (Math.abs(d) < Math.abs(bestDy)) {
                        bestDy = d;
                        guideY = t;
                    }
                });
            });
            if (Math.abs(bestDy) <= threshold) {
                nextY = y + bestDy;
            } else {
                guideY = null;
            }

            return { x: nextX, y: nextY, guideX, guideY };
        },
        [autoAlign, canvasHeight, canvasWidth, el.h, el.w, otherElements],
    );

    const onPointerDown = (e, m = "move") => {
        e.stopPropagation();
        onSelect(el.id);
        mode.current = m;
        startRef.current = {
            x: e.clientX,
            y: e.clientY,
            ox: el.x,
            oy: el.y,
            ow: el.w,
            oh: el.h,
            or: el.rotation || 0,
            // center of element in canvas coords (used for rotation handle math)
            cx: el.x + el.w / 2,
            cy: el.y + el.h / 2,
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };
    const onMove = (e) => {
        const s = startRef.current;
        if (!s) return;
        const dx = (e.clientX - s.x) / scale;
        const dy = (e.clientY - s.y) / scale;
        if (mode.current === "move") {
            const rawX = s.ox + dx;
            const rawY = s.oy + dy;
            const snapped = snapMove(rawX, rawY);
            onAlignGuideChange?.({ x: snapped.guideX, y: snapped.guideY });
            // Use rotated axis-aligned bounding box for clamping so a rotated
            // element can be dragged so its visual edge touches the canvas
            // border (otherwise wide rotated text gets clipped before reaching
            // the side).
            const rad = ((el.rotation || 0) * Math.PI) / 180;
            const cos = Math.abs(Math.cos(rad));
            const sin = Math.abs(Math.sin(rad));
            const aabbW = el.w * cos + el.h * sin;
            const aabbH = el.w * sin + el.h * cos;
            const minX = (aabbW - el.w) / 2; // negative when rotW < el.w
            const maxX = canvasWidth - (aabbW + el.w) / 2;
            const minY = (aabbH - el.h) / 2;
            const maxY = canvasHeight - (aabbH + el.h) / 2;
            onChange({
                ...el,
                x: Math.max(
                    Math.min(minX, maxX),
                    Math.min(Math.max(minX, maxX), snapped.x),
                ),
                y: Math.max(
                    Math.min(minY, maxY),
                    Math.min(Math.max(minY, maxY), snapped.y),
                ),
            });
        } else if (mode.current === "rotate") {
            // Rotation handle drag: angle from element center to current pointer
            const elNode = document.querySelector(`[data-el-id='${el.id}']`);
            if (!elNode) return;
            const r = elNode.getBoundingClientRect();
            const centerX = r.left + r.width / 2;
            const centerY = r.top + r.height / 2;
            const ang =
                (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) /
                    Math.PI +
                90; // +90 so handle pointing north = 0deg
            const snap = e.shiftKey ? 15 : 1;
            const snapped = Math.round(ang / snap) * snap;
            onChange({ ...el, rotation: ((snapped + 540) % 360) - 180 });
        } else if (mode.current.startsWith("resize-")) {
            onAlignGuideChange?.(null);
            let nx = s.ox,
                ny = s.oy,
                nw = s.ow,
                nh = s.oh;
            if (mode.current.includes("e")) nw = Math.max(10, s.ow + dx);
            if (mode.current.includes("s")) nh = Math.max(10, s.oh + dy);
            if (mode.current.includes("w")) {
                nw = Math.max(10, s.ow - dx);
                nx = s.ox + dx;
            }
            if (mode.current.includes("n")) {
                nh = Math.max(10, s.oh - dy);
                ny = s.oy + dy;
            }
            onChange({
                ...el,
                x: Math.round(nx * 10) / 10,
                y: Math.round(ny * 10) / 10,
                w: Math.round(nw * 10) / 10,
                h: Math.round(nh * 10) / 10,
            });
        }
    };
    const onUp = () => {
        startRef.current = null;
        onAlignGuideChange?.(null);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
    };

    const resolve = useCallback(
        (raw) => {
            if (!raw) return "";
            if (!row) return raw;
            return String(raw).replace(
                /\{\{\s*([^}]+)\s*\}\}/g,
                (_m, k) => row[k.trim()] ?? k,
            );
        },
        [row],
    );

    const commonStyle = {
        position: "absolute",
        left: el.x,
        top: el.y,
        width: el.w,
        height: el.h,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        zIndex: el.z || 1,
        cursor: "move",
    };

    // resolve photo src: bulk photo map has precedence > manually set src
    const photoKey = row && el.bind ? String(row[el.bind] ?? "").trim() : "";
    const mappedPhoto =
        photoKey && photoMap
            ? photoMap[photoKey] || photoMap[photoKey.toLowerCase()]
            : null;

    let content = null;
    if (el.kind === "text") {
        const boundText =
            el.bind && row ? String(row[el.bind] ?? el.text) : resolve(el.text);
        // If the text has no explicit newline, render it on a single line so
        // sub-pixel font-metric drift between the on-screen DOM and the export
        // rasterizer can never cause it to wrap to a 2nd line. Multi-line
        // text (with \n) keeps the original wrap behaviour.
        const isMultiline = /\r|\n/.test(boundText);
        content = (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        el.align === "center"
                            ? "center"
                            : el.align === "right"
                              ? "flex-end"
                              : "flex-start",
                    color: el.color,
                    background: el.bg || "transparent",
                    fontSize: el.fontSize,
                    fontFamily: el.fontFamily,
                    fontWeight: el.fontWeight,
                    padding: "2px 4px",
                    overflow: "hidden",
                    lineHeight: 1.15,
                }}
            >
                <span
                    style={{
                        whiteSpace: isMultiline ? "pre-wrap" : "nowrap",
                        wordBreak: isMultiline ? "break-word" : "normal",
                    }}
                >
                    {boundText}
                </span>
            </div>
        );
    } else if (el.kind === "photo") {
        const imgSrc =
            mappedPhoto ||
            (row && el.bind && /^(data:|https?:)/.test(row[el.bind] || "")
                ? row[el.bind]
                : "") ||
            el.src;
        content = (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    border: `${el.border}px solid ${el.borderColor}`,
                    borderRadius: el.radius,
                    overflow: "hidden",
                    background: "#f4f4f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {imgSrc ? (
                    <img
                        src={imgSrc}
                        alt=""
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                ) : (
                    <span className="text-[10px] text-zinc-500 font-mono uppercase">
                        Photo
                    </span>
                )}
            </div>
        );
    } else if (el.kind === "image") {
        content = el.src ? (
            <img
                src={el.src}
                alt=""
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: el.radius,
                }}
            />
        ) : (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    border: "1px dashed #a1a1aa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <span className="text-[10px] text-zinc-500 font-mono uppercase">
                    Image
                </span>
            </div>
        );
    } else if (el.kind === "signature") {
        content = el.src ? (
            <img
                src={el.src}
                alt=""
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: el.radius,
                    opacity: typeof el.opacity === "number" ? el.opacity : 1,
                    mixBlendMode: el.blend || "normal",
                }}
            />
        ) : (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    border: "1px dashed #a1a1aa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <span className="text-[10px] text-zinc-500 font-mono uppercase">
                    Signature
                </span>
            </div>
        );
    } else if (el.kind === "rect") {
        content = (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    background: el.bg,
                    borderRadius: el.radius,
                }}
            />
        );
    } else if (el.kind === "qr") {
        const val = resolve(el.value) || "QR";
        content = (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    background: el.bg,
                    padding: 2,
                }}
            >
                <QRRender value={val} color={el.color} bg={el.bg} />
            </div>
        );
    } else if (el.kind === "barcode") {
        const val = resolve(el.value) || "0000";
        content = (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    background: el.bg,
                    padding: 2,
                }}
            >
                <BarcodeRender
                    value={val}
                    format={el.format}
                    color={el.color}
                    bg={el.bg}
                    showText={el.showText}
                />
            </div>
        );
    }

    return (
        <div
            style={commonStyle}
            data-el-id={el.id}
            data-el-root
            onPointerDown={(e) => onPointerDown(e, "move")}
            className={selected ? "selected-el" : ""}
            data-testid={`canvas-el-${el.id}`}
        >
            {content}
            {selected && (
                <>
                    <span
                        className="resize-handle rh-nw"
                        onPointerDown={(e) => onPointerDown(e, "resize-nw")}
                    />
                    <span
                        className="resize-handle rh-ne"
                        onPointerDown={(e) => onPointerDown(e, "resize-ne")}
                    />
                    <span
                        className="resize-handle rh-sw"
                        onPointerDown={(e) => onPointerDown(e, "resize-sw")}
                    />
                    <span
                        className="resize-handle rh-se"
                        onPointerDown={(e) => onPointerDown(e, "resize-se")}
                    />
                    {/* Rotation handle (top center, 22px above element) */}
                    <span
                        onPointerDown={(e) => onPointerDown(e, "rotate")}
                        title="Drag to rotate · hold Shift for 15° snap"
                        style={{
                            position: "absolute",
                            left: "50%",
                            top: -26,
                            width: 14,
                            height: 14,
                            marginLeft: -7,
                            background: "#7c3aed",
                            border: "2px solid white",
                            borderRadius: "50%",
                            cursor: "grab",
                            boxShadow: "0 0 0 1px rgba(124,58,237,0.5)",
                        }}
                        data-testid="el-rotate-handle"
                    />
                    {/* Connector line from rotation handle to element top */}
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            top: -12,
                            width: 1,
                            height: 12,
                            marginLeft: -0.5,
                            background: "#7c3aed",
                            pointerEvents: "none",
                        }}
                    />
                    {/* Floating delete */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(el.id);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="absolute -top-7 right-0 bg-red-600 hover:bg-red-700 text-white p-1 shadow-lg"
                        style={{
                            transform: el.rotation
                                ? `rotate(${-el.rotation}deg)`
                                : undefined,
                        }}
                        data-testid="floating-delete-btn"
                        title="Delete (Del)"
                    >
                        <Trash size={11} weight="bold" />
                    </button>
                </>
            )}
        </div>
    );
}

// --- Main Studio --------------------------------------------------------------------
export default function Studio() {
    const { tid } = useParams();
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [tpl, setTpl] = useState(null);
    const [side, setSide] = useState("front");
    const [selId, setSelId] = useState(null);
    const [zoom, setZoom] = useState(2);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [rowIndex, setRowIndex] = useState(0);
    const [photoCount, setPhotoCount] = useState(0);
    const [photoMap, setPhotoMap] = useState({}); // filename -> dataURL
    const [signatureSrc, setSignatureSrc] = useState("");
    const [removingSignatureBg, setRemovingSignatureBg] = useState(false);
    const [autoAlign, setAutoAlign] = useState(true);
    const [alignGuides, setAlignGuides] = useState(null);
    const [showTour, setShowTour] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [trialWatermark, setTrialWatermark] = useState("");
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [uploadingPhotos, setUploadingPhotos] = useState(null); // {done, total} | null
    const canvasRef = useRef(null);

    const TOUR_STEPS = [
        {
            title: "Welcome to your first design",
            text: "Use the left toolbar to add text, photo, signature, QR and barcode elements.",
        },
        {
            title: "Use smart alignment",
            text: "Drag elements and watch guide lines for auto align suggestions. Arrow keys nudge selected elements.",
        },
        {
            title: "Background and exports",
            text: "Pick preset backgrounds or upload your own, then save and export PDF/PNG/Print.",
        },
    ];

    useEffect(() => {
        const seenTour = localStorage.getItem("idcardstudio_studio_tour_seen");
        if (!seenTour) {
            setShowTour(true);
        }

        api.get(`/templates/${tid}`)
            .then(({ data }) => {
                setTpl(data);
                // Restore previously-uploaded student photos & excel data from the saved template
                if (
                    data.student_photos &&
                    Object.keys(data.student_photos).length
                ) {
                    setPhotoMap(data.student_photos);
                    // photo count = unique images (stored under 4 keys per file)
                    const uniq = new Set(Object.values(data.student_photos));
                    setPhotoCount(uniq.size);
                }
                if (Array.isArray(data.data_columns))
                    setColumns(data.data_columns);
                if (Array.isArray(data.data_rows)) setRows(data.data_rows);
                const allElements = [
                    ...(data.front_elements || []),
                    ...(data.back_elements || []),
                ];
                const existingSignature = allElements.find(
                    (el) => el.kind === "signature" && el.src,
                );
                if (existingSignature?.src)
                    setSignatureSrc(existingSignature.src);
            })
            .catch(() => {
                toast.error("Project not found");
                nav("/dashboard");
            });
        refresh(); // always fetch latest credit balance when entering studio
    }, [tid, nav]); // eslint-disable-line

    useEffect(() => {
        // Prefer the trial flag directly from the user payload (no race with /packages).
        if (user?.is_trial_active) {
            setTrialWatermark(user.active_watermark_text || "idcardgen.com");
            return;
        }
        const activePackageId = user?.active_package_id;
        if (!activePackageId) {
            setTrialWatermark("");
            return;
        }

        api.get("/packages")
            .then(({ data }) => {
                const activePkg = (data || []).find(
                    (p) => p.id === activePackageId,
                );
                if (activePkg?.is_trial) {
                    setTrialWatermark(
                        activePkg?.watermark_text || "idcardgen.com",
                    );
                } else {
                    setTrialWatermark("");
                }
            })
            .catch(() => setTrialWatermark(""));
    }, [
        user?.active_package_id,
        user?.is_trial_active,
        user?.active_watermark_text,
    ]);

    const baseWidth = Math.round((tpl?.width_mm || 86) * MM_PER_PX);
    const baseHeight = Math.round((tpl?.height_mm || 54) * MM_PER_PX);
    const cw = Math.round(baseWidth * zoom);
    const ch = Math.round(baseHeight * zoom);

    const currentElements = tpl
        ? side === "front"
            ? tpl.front_elements
            : tpl.back_elements
        : [];
    const setCurrentElements = (list) => {
        setTpl((t) => ({
            ...t,
            [side === "front" ? "front_elements" : "back_elements"]: list,
        }));
    };
    const selected = currentElements.find((e) => e.id === selId);

    const addEl = (el) => {
        setCurrentElements([...(currentElements || []), el]);
        setSelId(el.id);
    };
    const updateEl = (next) => {
        setCurrentElements(
            currentElements.map((e) => (e.id === next.id ? next : e)),
        );
    };
    const deleteEl = (id) => {
        setCurrentElements(currentElements.filter((e) => e.id !== id));
        if (selId === id) setSelId(null);
    };
    const moveSelectedBy = (dx, dy) => {
        if (!selId) return;
        const maxX = Math.max(0, baseWidth);
        const maxY = Math.max(0, baseHeight);
        setCurrentElements(
            (currentElements || []).map((item) => {
                if (item.id !== selId) return item;
                return {
                    ...item,
                    x: Math.max(0, Math.min(maxX - item.w, item.x + dx)),
                    y: Math.max(0, Math.min(maxY - item.h, item.y + dy)),
                };
            }),
        );
    };

    // Keyboard delete
    useEffect(() => {
        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
                return;
            if ((e.key === "Delete" || e.key === "Backspace") && selId) {
                e.preventDefault();
                deleteEl(selId);
            }
            const step = e.shiftKey ? 10 : 1;
            if (e.key === "ArrowUp" && selId) {
                e.preventDefault();
                moveSelectedBy(0, -step);
            }
            if (e.key === "ArrowDown" && selId) {
                e.preventDefault();
                moveSelectedBy(0, step);
            }
            if (e.key === "ArrowLeft" && selId) {
                e.preventDefault();
                moveSelectedBy(-step, 0);
            }
            if (e.key === "ArrowRight" && selId) {
                e.preventDefault();
                moveSelectedBy(step, 0);
            }
            if (e.key === "Escape") {
                setSelId(null);
                setAlignGuides(null);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selId, currentElements, moveSelectedBy]); // eslint-disable-line

    const onFileToBase64 = (file) =>
        new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(file);
        });

    const stripLightBgToTransparent = useCallback(
        (dataUrl) =>
            new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    try {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.naturalWidth || img.width;
                        canvas.height = img.naturalHeight || img.height;
                        const w = canvas.width;
                        const h = canvas.height;
                        const ctx = canvas.getContext("2d", {
                            willReadFrequently: true,
                        });
                        ctx.drawImage(img, 0, 0);
                        const frame = ctx.getImageData(0, 0, w, h);
                        const px = frame.data;

                        const idx = (x, y) => (y * w + x) * 4;
                        const edges = [];
                        const step = Math.max(
                            1,
                            Math.floor(Math.min(w, h) / 80),
                        );

                        for (let x = 0; x < w; x += step) {
                            edges.push([x, 0], [x, h - 1]);
                        }
                        for (let y = 0; y < h; y += step) {
                            edges.push([0, y], [w - 1, y]);
                        }

                        let sr = 0,
                            sg = 0,
                            sb = 0,
                            count = 0;
                        for (const [x, y] of edges) {
                            const i = idx(x, y);
                            if (px[i + 3] < 10) continue;
                            sr += px[i];
                            sg += px[i + 1];
                            sb += px[i + 2];
                            count += 1;
                        }
                        if (!count) {
                            resolve(dataUrl);
                            return;
                        }

                        const bgR = sr / count;
                        const bgG = sg / count;
                        const bgB = sb / count;
                        const bgLum = (bgR + bgG + bgB) / 3;

                        const colorTol = 62;
                        const lumTol = 58;
                        const colorTolSq = colorTol * colorTol;
                        const removableCandidate = new Uint8Array(w * h);
                        const removable = new Uint8Array(w * h);

                        for (let y = 0; y < h; y++) {
                            for (let x = 0; x < w; x++) {
                                const p = y * w + x;
                                const i = p * 4;
                                if (px[i + 3] < 10) continue;
                                const dr = px[i] - bgR;
                                const dg = px[i + 1] - bgG;
                                const db = px[i + 2] - bgB;
                                const d2 = dr * dr + dg * dg + db * db;
                                const lum = (px[i] + px[i + 1] + px[i + 2]) / 3;
                                if (
                                    d2 <= colorTolSq &&
                                    Math.abs(lum - bgLum) <= lumTol
                                ) {
                                    removableCandidate[p] = 1;
                                }
                            }
                        }

                        const queue = [];
                        const push = (x, y) => {
                            if (x < 0 || y < 0 || x >= w || y >= h) return;
                            const p = y * w + x;
                            if (!removableCandidate[p] || removable[p]) return;
                            removable[p] = 1;
                            queue.push(p);
                        };

                        for (let x = 0; x < w; x++) {
                            push(x, 0);
                            push(x, h - 1);
                        }
                        for (let y = 0; y < h; y++) {
                            push(0, y);
                            push(w - 1, y);
                        }

                        while (queue.length) {
                            const p = queue.pop();
                            const x = p % w;
                            const y = (p - x) / w;
                            push(x + 1, y);
                            push(x - 1, y);
                            push(x, y + 1);
                            push(x, y - 1);
                        }

                        for (let p = 0; p < removable.length; p++) {
                            if (!removable[p]) continue;
                            const i = p * 4;
                            px[i + 3] = 0;
                        }

                        for (let y = 1; y < h - 1; y++) {
                            for (let x = 1; x < w - 1; x++) {
                                const p = y * w + x;
                                const i = p * 4;
                                if (px[i + 3] < 10 || removable[p]) continue;
                                const nearBg =
                                    removable[p - 1] ||
                                    removable[p + 1] ||
                                    removable[p - w] ||
                                    removable[p + w];
                                if (!nearBg) continue;
                                const dr = px[i] - bgR;
                                const dg = px[i + 1] - bgG;
                                const db = px[i + 2] - bgB;
                                const d2 = dr * dr + dg * dg + db * db;
                                if (d2 < colorTolSq * 1.3) {
                                    px[i + 3] = Math.round(px[i + 3] * 0.35);
                                }
                            }
                        }

                        ctx.putImageData(frame, 0, 0);
                        resolve(canvas.toDataURL("image/png"));
                    } catch (err) {
                        reject(err);
                    }
                };
                img.onerror = () => reject(new Error("Image decode failed"));
                img.src = dataUrl;
            }),
        [],
    );

    const addImage = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        e.target.value = "";
        const b64 = await onFileToBase64(f);
        addEl(DEFAULT_IMAGE(b64));
    };
    const setBackground = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        e.target.value = "";
        const b64 = await onFileToBase64(f);
        const key = side === "front" ? "front_background" : "back_background";
        setTpl((t) => ({ ...t, [key]: { src: b64 } }));
    };
    const clearBackground = () => {
        const key = side === "front" ? "front_background" : "back_background";
        setTpl((t) => ({ ...t, [key]: null }));
    };
    const applyPresetBackground = (src) => {
        const key = side === "front" ? "front_background" : "back_background";
        setTpl((t) => ({ ...t, [key]: { src } }));
    };
    const bgSrc =
        (side === "front"
            ? tpl?.front_background?.src
            : tpl?.back_background?.src) || "";

    const uploadExcel = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        e.target.value = "";
        const form = new FormData();
        form.append("file", f);
        try {
            const { data } = await api.post("/bulk/parse-excel", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setColumns(data.columns);
            setRows(data.rows);
            setRowIndex(0);
            toast.success(`${data.rows.length} rows loaded`);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        }
    };

    const downloadSampleExcel = () => {
        // Build a sample workbook with a Data sheet + an Instructions sheet
        const headers = [
            "Photo",
            "StudentName",
            "FatherName",
            "Class",
            "RegNo",
            "DOB",
            "Address",
            "BloodGroup",
            "Mobile",
        ];
        const rows = [
            [
                "23.jpg",
                "Ravi Kumar",
                "Suresh Kumar",
                "10-A",
                "REG2026001",
                "2010-04-15",
                "Patna, Bihar",
                "O+",
                "9876543210",
            ],
            [
                "24.jpg",
                "Priya Singh",
                "Ramesh Singh",
                "10-A",
                "REG2026002",
                "2010-07-08",
                "Patna, Bihar",
                "A+",
                "9876543211",
            ],
            [
                "25.jpg",
                "Arjun Sharma",
                "Vikas Sharma",
                "10-B",
                "REG2026003",
                "2010-02-22",
                "Patna, Bihar",
                "B+",
                "9876543212",
            ],
        ];
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws["!cols"] = headers.map(() => ({ wch: 16 }));

        const instructions = [
            ["📖 IDCardGen — Sample Excel Instructions"],
            [""],
            ["1. Each row = one ID card."],
            [
                "2. Column names (header row) become bindable fields in the Studio.",
            ],
            [
                "   In the Studio, edit a text element and select 'Bind to column' to insert that value.",
            ],
            [""],
            ["📷 HOW TO LINK PHOTOS"],
            ["1. Add a 'Photo' column (or any name) to this sheet."],
            [
                "2. Put the file name of each student's photo in that column. Example: 23.jpg, 24.png, ravi.jpg",
            ],
            [
                "3. In the Studio sidebar -> 'Student Photos' -> Upload your image files (you can select many at once).",
            ],
            [
                "4. The file name must EXACTLY match the value in the Photo column (case-insensitive).",
            ],
            [
                "   Example: cell value '23.jpg' will match the uploaded file '23.jpg'.",
            ],
            [
                "5. Add a Photo element on the card and bind it to the 'Photo' column.",
            ],
            [""],
            ["⚠️ TIPS"],
            ["• Save this file as .xlsx (not .xls)."],
            ["• Maximum 5000 rows per upload."],
            [
                "• Keep header names short and without spaces (use RegNo, not 'Reg No').",
            ],
            ["• Dates: any text format works — the value is printed as-is."],
            [
                "• For QR / Barcode: bind them to a column (e.g. RegNo) to print one unique code per card.",
            ],
            [""],
            ["🌐 Need help? Visit https://idcardgen.com/help"],
        ];
        const wsInfo = XLSX.utils.aoa_to_sheet(instructions);
        wsInfo["!cols"] = [{ wch: 100 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data");
        XLSX.utils.book_append_sheet(wb, wsInfo, "Instructions");
        XLSX.writeFile(wb, "idcardgen-sample.xlsx");
        toast.success("Sample Excel downloaded");
    };

    // Bulk photo upload — filename used as key (e.g., "10.png").
    //
    // Photos are uploaded to the server in CHUNKS of 10 immediately on
    // selection. The server resizes + writes each one to disk and returns
    // a URL; we keep only the URL in photoMap. This means:
    //   * the browser never holds 200 base64 strings (~16 MB) in state, so
    //     subsequent template saves don't trigger a multi-second JSON
    //     stringify that triggers Chrome's "Page Unresponsive" dialog;
    //   * PHP-FPM workers process small batches (~2 s each) instead of
    //     one 30+ second mega-save that blocks other users.
    const uploadPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        if (!tid) return toast.error("Save the template first");

        setUploadingPhotos({ done: 0, total: files.length });
        const map = { ...photoMap };
        const indexInto = (key, value) => {
            map[key] = value;
            map[key.toLowerCase()] = value;
            const base = key.replace(/\.[^.]+$/, "");
            map[base] = value;
            map[base.toLowerCase()] = value;
        };

        const CHUNK = 10;
        try {
            for (let i = 0; i < files.length; i += CHUNK) {
                const slice = files.slice(i, i + CHUNK);
                // Read this chunk's files to base64 in parallel.
                const chunkB64 = {};
                await Promise.all(
                    slice.map(async (f) => {
                        chunkB64[f.name] = await onFileToBase64(f);
                    }),
                );
                // POST the chunk; server returns URL map.
                const { data } = await api.post(`/templates/${tid}/photos`, {
                    photos: chunkB64,
                });
                Object.entries(data.photos || {}).forEach(([k, v]) =>
                    indexInto(k, v),
                );
                setPhotoMap({ ...map });
                setUploadingPhotos({
                    done: Math.min(i + slice.length, files.length),
                    total: files.length,
                });
                // yield to the event loop between chunks
                await new Promise((r) => setTimeout(r, 0));
            }
            toast.success(
                `${files.length} photo${files.length > 1 ? "s" : ""} uploaded`,
            );
        } catch (err) {
            toast.error(
                "Photo upload failed: " +
                    (formatApiErrorDetail(err.response?.data?.detail) ||
                        err.message),
            );
        } finally {
            setUploadingPhotos(null);
        }
    };

    const uploadSignature = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const b64 = await onFileToBase64(f);
        setSignatureSrc(b64);
        toast.success("Principal signature loaded");
    };

    const removeSignatureBackground = async () => {
        if (!signatureSrc) return;
        setRemovingSignatureBg(true);
        try {
            const oldSrc = signatureSrc;
            const cleaned = await stripLightBgToTransparent(signatureSrc);
            setSignatureSrc(cleaned);
            setTpl((t) => ({
                ...t,
                front_elements: (t?.front_elements || []).map((el) =>
                    el.kind === "signature" && el.src === oldSrc
                        ? { ...el, src: cleaned }
                        : el,
                ),
                back_elements: (t?.back_elements || []).map((el) =>
                    el.kind === "signature" && el.src === oldSrc
                        ? { ...el, src: cleaned }
                        : el,
                ),
            }));
            toast.success("Signature background removed");
        } catch {
            toast.error("Could not remove background for this image");
        } finally {
            setRemovingSignatureBg(false);
        }
    };

    const saveTpl = async () => {
        if (!tpl) return;
        setSaving(true);
        try {
            // Strip out any inline base64 photos before sending — they're
            // already on the server (uploaded in chunks via /photos endpoint).
            // This keeps the save payload tiny (a few KB) so the browser
            // never freezes serializing 16 MB of base64, and PHP doesn't
            // re-process the same images on every save.
            const slimPhotoMap = {};
            Object.entries(photoMap || {}).forEach(([k, v]) => {
                if (typeof v === "string" && !v.startsWith("data:")) {
                    slimPhotoMap[k] = v;
                }
            });
            const payload = {
                name: tpl.name,
                width_mm: tpl.width_mm,
                height_mm: tpl.height_mm,
                layout: tpl.layout,
                card_type: tpl.card_type,
                front_elements: tpl.front_elements || [],
                back_elements: tpl.back_elements || [],
                front_background: tpl.front_background || null,
                back_background: tpl.back_background || null,
                student_photos: slimPhotoMap,
                data_columns: columns || [],
                data_rows: rows || [],
            };
            await api.put(`/templates/${tid}`, payload);
            toast.success("Saved — photos and data preserved");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setSaving(false);
        }
    };

    const snapshot = async (pixelRatio = 3, format = "png", quality = 0.95) => {
        // Deselect any active element so resize / rotate / delete handles
        // don't end up baked into the exported image.
        const prevSel = selId;
        if (prevSel) setSelId(null);
        // wait for DOM updates AND web-fonts so text metrics are stable
        // (prevents single-line names like "Patil Priya M." wrapping at export)
        await new Promise((r) =>
            requestAnimationFrame(() => requestAnimationFrame(r)),
        );
        if (typeof document !== "undefined" && document.fonts?.ready) {
            try {
                await document.fonts.ready;
            } catch (_e) {
                // ignore — best-effort
            }
        }
        await new Promise((r) => setTimeout(r, 120));
        // Force overflow:hidden on the print-area for the duration of the
        // capture so handles / spillover from the editing UI cannot leak
        // into the snapshot. We restore the inline style afterwards.
        const node = canvasRef.current;
        const prevOverflow = node?.style.overflow;
        if (node) node.style.overflow = "hidden";
        const opts = {
            pixelRatio,
            cacheBust: true,
            backgroundColor: "#fff",
            skipFonts: false,
            quality,
        };
        try {
            return await (format === "jpeg"
                ? toJpeg(node, opts)
                : toPng(node, opts));
        } finally {
            if (node) node.style.overflow = prevOverflow || "";
            if (prevSel) setSelId(prevSel);
        }
    };

    // ---- A4 grid layout: 2 columns x 4 rows = 8 cards per page ----
    const A4_W = 210; // mm
    const A4_H = 297; // mm
    const GRID_COLS = 2;
    const GRID_ROWS = 4;
    const GRID_PER_PAGE = GRID_COLS * GRID_ROWS;
    const GRID_MARGIN_X = 10; // mm
    const GRID_MARGIN_Y = 10; // mm
    const GRID_GAP_X = 5; // mm
    const GRID_GAP_Y = 5; // mm

    const computeGridCell = () => {
        const cellW =
            (A4_W - GRID_MARGIN_X * 2 - GRID_GAP_X * (GRID_COLS - 1)) /
            GRID_COLS;
        const cellH =
            (A4_H - GRID_MARGIN_Y * 2 - GRID_GAP_Y * (GRID_ROWS - 1)) /
            GRID_ROWS;
        const scale = Math.min(cellW / tpl.width_mm, cellH / tpl.height_mm);
        const cardW = tpl.width_mm * scale;
        const cardH = tpl.height_mm * scale;
        return { cellW, cellH, cardW, cardH };
    };

    const placeOnGrid = (pdf, dataUrl, idxOnPage, geom, imgFmt = "PNG") => {
        const { cardW, cardH } = geom;
        const col = idxOnPage % GRID_COLS;
        const row = Math.floor(idxOnPage / GRID_COLS);
        // Pack cards top-left at their actual size so inter-card gaps stay
        // uniform (no big empty centring space between rows).
        const x = GRID_MARGIN_X + col * (cardW + GRID_GAP_X);
        const y = GRID_MARGIN_Y + row * (cardH + GRID_GAP_Y);
        pdf.addImage(dataUrl, imgFmt, x, y, cardW, cardH, undefined, "FAST");
    };

    const exportCurrent = async (format) => {
        if (!canvasRef.current) return toast.error("Canvas not ready");
        setSelId(null);
        setExporting(true);
        const isTrial = Boolean(trialWatermark);
        try {
            if (!isTrial) {
                await api.post("/cards/consume", {
                    count: 1,
                    template_id: tid,
                });
                await refresh();
            }
            const dataUrl = await snapshot();
            if (!dataUrl || dataUrl.length < 100)
                throw new Error(
                    "Snapshot failed — please reload the page and try again",
                );
            if (format === "png") {
                const a = document.createElement("a");
                a.download = `${tpl.name || "card"}-${side}.png`;
                a.href = dataUrl;
                a.click();
            } else if (format === "pdf") {
                const pdf = new jsPDF({
                    orientation: "p",
                    unit: "mm",
                    format: "a4",
                    compress: false,
                });
                const geom = computeGridCell();
                placeOnGrid(pdf, dataUrl, 0, geom);
                pdf.save(`${tpl.name || "card"}-${side}.pdf`);
            } else if (format === "print") {
                const w = window.open("");
                w.document.write(
                    `<html><head><title>Print ${tpl.name}</title><style>@page{size:${tpl.width_mm}mm ${tpl.height_mm}mm;margin:0;}body{margin:0;padding:0;}img{width:${tpl.width_mm}mm;height:${tpl.height_mm}mm;}</style></head><body><img src="${dataUrl}" onload="window.print();window.close();" /></body></html>`,
                );
                w.document.close();
            }
            toast.success(
                isTrial
                    ? "Exported (trial — watermark applied)"
                    : "Exported — 1 credit used",
            );
        } catch (err) {
            const msg =
                formatApiErrorDetail(err.response?.data?.detail) ||
                err.message ||
                "Export failed";
            toast.error(msg);
            console.error("Export error", err);
        } finally {
            setExporting(false);
        }
    };

    const exportBulk = async (format) => {
        if (rows.length === 0) return toast.error("Upload Excel data first");
        const available = user?.credits_remaining ?? 0;
        const isAdmin = user?.role === "admin";
        const isTrial = Boolean(trialWatermark);
        if (!isAdmin && !isTrial && available < rows.length) {
            return toast.error(
                `Not enough credits: need ${rows.length}, have ${available}. Buy a larger package from Pricing.`,
            );
        }
        const ok = window.confirm(
            `Export ${rows.length} card${rows.length > 1 ? "s" : ""} as ${format === "pdf" ? "a single PDF" : "individual PNGs"}?\n\n` +
                (isTrial
                    ? `Trial export — every card will include the "${trialWatermark}" watermark.`
                    : `This will use ${rows.length} credit${rows.length > 1 ? "s" : ""}${isAdmin ? " (admin — unlimited)" : ` (you have ${available})`}.`),
        );
        if (!ok) return;
        setExporting(true);
        setSelId(null);
        setBulkProgress({ done: 0, total: rows.length });
        try {
            if (!isTrial) {
                await api.post("/cards/consume", {
                    count: rows.length,
                    template_id: tid,
                });
                await refresh();
            }
            if (format === "pdf") {
                const pdf = new jsPDF({
                    orientation: "p",
                    unit: "mm",
                    format: "a4",
                    compress: true,
                });
                const geom = computeGridCell();
                // Pixel ratio scales with batch size: large batches drop to 2
                // to avoid browser OOM crashes; small batches stay crisp at 3.
                const bulkPixelRatio = rows.length > 200 ? 2 : 3;
                for (let i = 0; i < rows.length; i++) {
                    setRowIndex(i);
                    setBulkProgress({ done: i, total: rows.length });
                    // JPEG @ quality 0.92 → great print quality, manageable file size
                    const dataUrl = await snapshot(
                        bulkPixelRatio,
                        "jpeg",
                        0.92,
                    );
                    const idxOnPage = i % GRID_PER_PAGE;
                    if (i > 0 && idxOnPage === 0) pdf.addPage("a4", "p");
                    placeOnGrid(pdf, dataUrl, idxOnPage, geom, "JPEG");
                    // Yield to the browser. Every card we hand control back
                    // for a real frame; every 10 cards we sleep a bit longer
                    // so Chrome's "unresponsive" timer (5s of blocked main
                    // thread) never fires on big batches.
                    await new Promise((r) =>
                        requestAnimationFrame(() => setTimeout(r, 0)),
                    );
                    if (i > 0 && i % 10 === 0) {
                        await new Promise((r) => setTimeout(r, 60));
                    }
                }
                pdf.save(`${tpl.name}-bulk-${side}.pdf`);
            } else if (format === "zip-png") {
                for (let i = 0; i < rows.length; i++) {
                    setRowIndex(i);
                    setBulkProgress({ done: i, total: rows.length });
                    const dataUrl = await snapshot();
                    const a = document.createElement("a");
                    a.download = `${tpl.name}-${i + 1}-${side}.png`;
                    a.href = dataUrl;
                    a.click();
                    await new Promise((r) =>
                        requestAnimationFrame(() => setTimeout(r, 80)),
                    );
                }
            }
            toast.success(
                isTrial
                    ? `Exported ${rows.length} cards (trial \u2014 watermark applied)`
                    : `Exported ${rows.length} cards \u2014 ${rows.length} credits used`,
            );
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setExporting(false);
            setBulkProgress(null);
        }
    };

    if (!tpl) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;
    const currentRow = rows[rowIndex];

    return (
        <div
            className="h-screen flex flex-col bg-zinc-100 overflow-hidden"
            data-testid="studio-page"
        >
            {/* Top bar */}
            <div className="h-14 bg-gradient-to-r from-indigo-700 via-violet-700 to-purple-700 text-white flex items-center justify-between px-4 border-b border-violet-900/40 shrink-0 shadow-lg shadow-violet-900/20">
                <div className="flex items-center gap-3 min-w-0">
                    <Link
                        to="/dashboard"
                        className="text-white/70 hover:text-white p-2 shrink-0"
                        data-testid="studio-back-btn"
                    >
                        <ArrowLeft size={18} />
                    </Link>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                            Project
                        </div>
                        <div className="font-display font-bold leading-none truncate">
                            {tpl.name}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="hidden md:flex px-3 py-1.5 text-xs font-mono border border-white/20 bg-white/5">
                        Credits:{" "}
                        <span className="text-blue-300 font-bold ml-1">
                            {user?.credits_remaining ?? 0}
                        </span>
                    </div>
                    <button
                        onClick={saveTpl}
                        disabled={saving}
                        className="bg-white/10 hover:bg-white/20 px-3 py-2 text-xs flex items-center gap-1.5"
                        data-testid="studio-save-btn"
                    >
                        <FloppyDisk size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                        onClick={() => exportCurrent("pdf")}
                        disabled={exporting}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 px-3 py-2 text-xs flex items-center gap-1.5 font-medium shadow-lg shadow-blue-500/30"
                        data-testid="studio-export-pdf"
                    >
                        <FilePdf size={14} weight="fill" /> PDF
                    </button>
                    <button
                        onClick={() => exportCurrent("png")}
                        disabled={exporting}
                        className="bg-white/10 hover:bg-white/20 px-3 py-2 text-xs flex items-center gap-1.5"
                        data-testid="studio-export-png"
                    >
                        <FileImage size={14} /> PNG
                    </button>
                    <button
                        onClick={() => exportCurrent("print")}
                        disabled={exporting}
                        className="bg-white/10 hover:bg-white/20 px-3 py-2 text-xs flex items-center gap-1.5"
                        data-testid="studio-print-btn"
                    >
                        <Printer size={14} /> Print
                    </button>
                </div>
            </div>

            {/* Bulk progress bar */}
            {bulkProgress && (
                <div className="h-1 bg-blue-100">
                    <div
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all"
                        style={{
                            width: `${(bulkProgress.done / bulkProgress.total) * 100}%`,
                        }}
                    />
                </div>
            )}

            <div className="flex-1 flex overflow-hidden">
                {/* Left toolbox */}
                <aside className="w-14 bg-gradient-to-b from-indigo-900 to-violet-900 border-r border-violet-950/40 flex flex-col py-3 items-center gap-1 shrink-0">
                    <ToolBtn
                        title="Text"
                        onClick={() => addEl(DEFAULT_TEXT())}
                        icon={TextT}
                    />
                    <ToolBtn
                        title="Bound Field"
                        onClick={() => addEl(DEFAULT_TEXT("placeholder"))}
                        icon={TextT}
                        badge="{}"
                    />
                    <ToolBtn
                        title="Photo"
                        onClick={() => addEl(DEFAULT_PHOTO())}
                        icon={UserIcon}
                    />
                    <ToolBtn
                        title="Signature"
                        onClick={() => addEl(DEFAULT_SIGNATURE(signatureSrc))}
                        icon={PenNib}
                    />
                    <label
                        title="Image"
                        className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 cursor-pointer transition-colors"
                    >
                        <ImgIcon size={18} />
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={addImage}
                            data-testid="add-image-input"
                        />
                    </label>
                    <ToolBtn
                        title="Rect"
                        onClick={() => addEl(DEFAULT_RECT())}
                        icon={Rectangle}
                    />
                    <ToolBtn
                        title="QR"
                        onClick={() => addEl(DEFAULT_QR())}
                        icon={QrCode}
                    />
                    <ToolBtn
                        title="Barcode"
                        onClick={() => addEl(DEFAULT_BARCODE())}
                        icon={Barcode}
                    />
                    <div className="mt-auto flex flex-col items-center gap-1">
                        <label
                            title="Set Background"
                            className="w-10 h-10 flex items-center justify-center text-pink-300 hover:text-pink-200 hover:bg-white/10 cursor-pointer transition-colors"
                        >
                            <Palette size={18} weight="duotone" />
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={setBackground}
                            />
                        </label>
                    </div>
                </aside>

                {/* Canvas area */}
                <main className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-zinc-100 via-blue-50 to-indigo-50">
                    <div className="h-11 bg-white border-b border-zinc-200 flex items-center px-4 gap-2 shrink-0">
                        <button
                            onClick={() => setSide("front")}
                            className={`px-3 py-1.5 text-xs font-medium border transition-all ${side === "front" ? "bg-gradient-to-r from-amber-400 to-yellow-400 border-amber-500 text-zinc-950 shadow-sm" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}
                            data-testid="tab-front"
                        >
                            Front Card
                        </button>
                        {tpl.card_type === "both" && (
                            <button
                                onClick={() => setSide("back")}
                                className={`px-3 py-1.5 text-xs font-medium border transition-all ${side === "back" ? "bg-gradient-to-r from-amber-400 to-yellow-400 border-amber-500 text-zinc-950 shadow-sm" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}
                                data-testid="tab-back"
                            >
                                Back Card
                            </button>
                        )}
                        {bgSrc && (
                            <button
                                onClick={clearBackground}
                                className="btn-ghost text-xs text-red-600"
                            >
                                × Remove bg
                            </button>
                        )}
                        <div className="ml-auto flex items-center gap-3">
                            {rows.length > 0 && (
                                <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 px-2 py-1 text-xs font-mono">
                                    <button
                                        className="p-1 hover:bg-white"
                                        onClick={() =>
                                            setRowIndex((i) =>
                                                Math.max(0, i - 1),
                                            )
                                        }
                                    >
                                        <CaretLeft size={14} />
                                    </button>
                                    <span className="text-blue-900 font-bold">
                                        Row {rowIndex + 1} / {rows.length}
                                    </span>
                                    <button
                                        className="p-1 hover:bg-white"
                                        onClick={() =>
                                            setRowIndex((i) =>
                                                Math.min(
                                                    rows.length - 1,
                                                    i + 1,
                                                ),
                                            )
                                        }
                                    >
                                        <CaretRight size={14} />
                                    </button>
                                </div>
                            )}
                            <div className="flex items-center gap-1 text-xs font-mono border border-zinc-200 bg-white">
                                <button
                                    className="px-2 py-1 hover:bg-zinc-100"
                                    onClick={() =>
                                        setZoom((z) =>
                                            Math.max(1, +(z - 0.25).toFixed(2)),
                                        )
                                    }
                                >
                                    −
                                </button>
                                <span className="w-12 text-center">
                                    {Math.round(zoom * 100)}%
                                </span>
                                <button
                                    className="px-2 py-1 hover:bg-zinc-100"
                                    onClick={() =>
                                        setZoom((z) =>
                                            Math.min(5, +(z + 0.25).toFixed(2)),
                                        )
                                    }
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        className="flex-1 overflow-auto canvas-grid relative"
                        onClick={() => {
                            setSelId(null);
                            setAlignGuides(null);
                        }}
                    >
                        <div className="min-h-full flex items-center justify-center p-10">
                            <div
                                id="print-area"
                                ref={canvasRef}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    width: cw,
                                    height: ch,
                                    background: "#fff",
                                    position: "relative",
                                    boxShadow:
                                        "0 0 0 1px #0A0A0A, 0 30px 80px rgba(37,99,235,0.15)",
                                    // Keep overflow visible so resize / rotate / delete
                                    // handles of elements placed near the card edge
                                    // (very common on vertical cards) don't get clipped.
                                    // The snapshot() helper temporarily switches this
                                    // back to `hidden` while exporting so handles never
                                    // bleed into the PDF/PNG.
                                    overflow: "visible",
                                }}
                                data-testid="design-canvas"
                            >
                                {bgSrc && (
                                    <img
                                        src={bgSrc}
                                        alt=""
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                )}
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        transform: `scale(${zoom})`,
                                        transformOrigin: "top left",
                                        width: baseWidth,
                                        height: baseHeight,
                                    }}
                                >
                                    {(currentElements || []).map((el) => (
                                        <CanvasEl
                                            key={el.id}
                                            el={el}
                                            row={currentRow}
                                            photoMap={photoMap}
                                            selected={selId === el.id}
                                            onSelect={setSelId}
                                            onChange={updateEl}
                                            onDelete={deleteEl}
                                            scale={zoom}
                                            otherElements={currentElements.filter(
                                                (x) => x.id !== el.id,
                                            )}
                                            autoAlign={autoAlign}
                                            onAlignGuideChange={setAlignGuides}
                                            canvasWidth={baseWidth}
                                            canvasHeight={baseHeight}
                                        />
                                    ))}
                                    {autoAlign && alignGuides?.x != null && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: alignGuides.x,
                                                top: 0,
                                                width: 1,
                                                height: "100%",
                                                background:
                                                    "rgba(37, 99, 235, 0.7)",
                                                pointerEvents: "none",
                                                zIndex: 999,
                                            }}
                                        />
                                    )}
                                    {autoAlign && alignGuides?.y != null && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: 0,
                                                top: alignGuides.y,
                                                width: "100%",
                                                height: 1,
                                                background:
                                                    "rgba(37, 99, 235, 0.7)",
                                                pointerEvents: "none",
                                                zIndex: 999,
                                            }}
                                        />
                                    )}
                                </div>
                                {trialWatermark && (
                                    <div
                                        aria-hidden="true"
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            pointerEvents: "none",
                                            zIndex: 1000,
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {/* Tiled diagonal watermark — visible on any background */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                inset: "-25%",
                                                transform: "rotate(-24deg)",
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "repeat(3, 1fr)",
                                                gridAutoRows: "1fr",
                                                gap: 12,
                                                opacity: 0.55,
                                            }}
                                        >
                                            {Array.from({ length: 12 }).map(
                                                (_, i) => (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            fontFamily:
                                                                "monospace",
                                                            fontWeight: 800,
                                                            fontSize: Math.max(
                                                                14,
                                                                Math.round(
                                                                    baseHeight *
                                                                        0.09,
                                                                ),
                                                            ),
                                                            color: "rgba(220, 38, 38, 0.55)",
                                                            letterSpacing: 2,
                                                            textTransform:
                                                                "uppercase",
                                                            whiteSpace:
                                                                "nowrap",
                                                            textShadow:
                                                                "0 0 2px rgba(255,255,255,0.6)",
                                                        }}
                                                    >
                                                        {trialWatermark}
                                                    </span>
                                                ),
                                            )}
                                        </div>
                                        {/* Big centered banner across the card */}
                                        <span
                                            style={{
                                                position: "relative",
                                                fontFamily: "monospace",
                                                fontWeight: 900,
                                                fontSize: Math.max(
                                                    20,
                                                    Math.round(
                                                        baseHeight * 0.16,
                                                    ),
                                                ),
                                                color: "rgba(220, 38, 38, 0.7)",
                                                transform: "rotate(-18deg)",
                                                letterSpacing: 4,
                                                textTransform: "uppercase",
                                                textShadow:
                                                    "0 0 4px rgba(255,255,255,0.85)",
                                            }}
                                        >
                                            {trialWatermark}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {currentElements.length === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-white/80 backdrop-blur border border-blue-200 px-5 py-3 text-sm text-zinc-600 flex items-center gap-2 shadow-lg">
                                    <Sparkle
                                        size={16}
                                        weight="fill"
                                        className="text-blue-600"
                                    />
                                    Empty canvas — use the left toolbar to add
                                    text, photo, signature, rectangle, QR or
                                    barcode
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                {/* Right properties panel */}
                <aside className="w-80 bg-white border-l border-zinc-200 overflow-y-auto shrink-0">
                    <div className="p-4 border-b border-zinc-200">
                        <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold mb-1">
                            Properties
                        </div>
                        <div className="mb-3 flex items-center justify-between text-xs bg-zinc-50 border border-zinc-200 p-2">
                            <span className="text-zinc-700">
                                Auto align suggestions
                            </span>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={autoAlign}
                                    onChange={(e) => {
                                        setAutoAlign(e.target.checked);
                                        if (!e.target.checked)
                                            setAlignGuides(null);
                                    }}
                                />
                                <span className="text-zinc-700">
                                    {autoAlign ? "On" : "Off"}
                                </span>
                            </label>
                        </div>
                        {!selected ? (
                            <div className="text-xs text-zinc-500 py-6 text-center border border-dashed border-zinc-200">
                                Select an element on the canvas.
                                <br />
                                Press{" "}
                                <kbd className="font-mono bg-zinc-100 px-1 border">
                                    Del
                                </kbd>{" "}
                                to delete.
                                <br />
                                Use arrow keys to move selected element (Shift =
                                10px).
                            </div>
                        ) : (
                            <ElementProperties
                                el={selected}
                                onChange={updateEl}
                                onDelete={() => deleteEl(selected.id)}
                                columns={columns}
                            />
                        )}
                    </div>

                    {/* Data sheet */}
                    <div className="p-4 border-b border-zinc-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">
                                Data Sheet
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={downloadSampleExcel}
                                    className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                                    title="Download a sample Excel with Photo column + instructions sheet"
                                    data-testid="download-sample-excel"
                                >
                                    <FileImage size={12} /> Sample
                                </button>
                                <label className="text-xs text-blue-600 cursor-pointer hover:underline flex items-center gap-1">
                                    <UploadSimple size={12} /> Upload Excel
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        hidden
                                        onChange={uploadExcel}
                                        data-testid="upload-excel-input"
                                    />
                                </label>
                            </div>
                        </div>
                        {rows.length === 0 ? (
                            <div className="text-xs text-zinc-500">
                                Upload an Excel file to bind columns to card
                                fields.{" "}
                                <button
                                    type="button"
                                    onClick={downloadSampleExcel}
                                    className="text-emerald-600 underline hover:text-emerald-700"
                                >
                                    Download sample
                                </button>{" "}
                                to see the format (incl. how to link photos).
                            </div>
                        ) : (
                            <>
                                <div className="text-[10px] font-mono text-zinc-500 truncate">
                                    Columns: {columns.join(", ")}
                                </div>
                                <div className="mt-2 max-h-40 overflow-auto border border-zinc-200">
                                    <table className="w-full text-[10px]">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                {columns.map((c) => (
                                                    <th
                                                        key={c}
                                                        className="p-1 text-left border-b"
                                                    >
                                                        {c}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.slice(0, 30).map((r, i) => (
                                                <tr
                                                    key={i}
                                                    className={
                                                        i === rowIndex
                                                            ? "bg-blue-50 cursor-pointer"
                                                            : "cursor-pointer hover:bg-zinc-50"
                                                    }
                                                    onClick={() =>
                                                        setRowIndex(i)
                                                    }
                                                >
                                                    {columns.map((c) => (
                                                        <td
                                                            key={c}
                                                            className="p-1 border-b truncate max-w-[60px]"
                                                        >
                                                            {r[c]}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Bulk student photos */}
                    <div className="p-4 border-b border-zinc-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">
                                Student Photos
                            </div>
                            <label
                                className={`text-xs cursor-pointer hover:underline flex items-center gap-1 ${uploadingPhotos ? "text-zinc-400 pointer-events-none" : "text-blue-600"}`}
                            >
                                <Images size={12} /> Upload
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    disabled={!!uploadingPhotos}
                                    onChange={uploadPhotos}
                                    data-testid="upload-photos-input"
                                />
                            </label>
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-2">
                            Select multiple photo files. Filenames (e.g.{" "}
                            <code className="bg-zinc-100 px-1">10.png</code>)
                            auto-match the <b>Photo</b> column values in your
                            Excel.
                        </div>
                        {uploadingPhotos && (
                            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 p-2 mb-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span>
                                        Uploading{" "}
                                        <b>
                                            {uploadingPhotos.done}/
                                            {uploadingPhotos.total}
                                        </b>{" "}
                                        photos…
                                    </span>
                                    <span className="font-mono">
                                        {Math.round(
                                            (uploadingPhotos.done /
                                                Math.max(
                                                    1,
                                                    uploadingPhotos.total,
                                                )) *
                                                100,
                                        )}
                                        %
                                    </span>
                                </div>
                                <div className="h-1 bg-blue-100 overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 transition-all"
                                        style={{
                                            width: `${(uploadingPhotos.done / Math.max(1, uploadingPhotos.total)) * 100}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {Object.keys(photoMap).length > 0 &&
                            !uploadingPhotos && (
                                <div className="flex items-center justify-between text-xs bg-green-50 border border-green-200 text-green-800 p-2">
                                    <span>
                                        <b>{photoCount}</b> photo
                                        {photoCount > 1 ? "s" : ""} loaded
                                    </span>
                                    <button
                                        onClick={() => {
                                            setPhotoMap({});
                                            setPhotoCount(0);
                                        }}
                                        className="text-red-600 hover:underline"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                    </div>

                    {/* Principal signature */}
                    <div className="p-4 border-b border-zinc-200">
                        <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold mb-2">
                            Background Designs
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-2">
                            Choose one of our designs or upload your own from
                            the left toolbar.
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {BG_PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    onClick={() =>
                                        applyPresetBackground(preset.src)
                                    }
                                    className="border border-zinc-200 hover:border-blue-400 overflow-hidden"
                                    title={preset.name}
                                >
                                    <img
                                        src={preset.src}
                                        alt={preset.name}
                                        className="w-full h-14 object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Principal signature */}
                    <div className="p-4 border-b border-zinc-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">
                                Principal Signature
                            </div>
                            <label className="text-xs text-blue-600 cursor-pointer hover:underline flex items-center gap-1">
                                <PenNib size={12} /> Upload
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={uploadSignature}
                                    data-testid="upload-signature-input"
                                />
                            </label>
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-2">
                            Upload principal signature, then add and drag it on
                            the card.
                        </div>
                        {signatureSrc ? (
                            <div className="space-y-2">
                                <div className="bg-zinc-50 border border-zinc-200 p-2 flex items-center justify-between">
                                    <img
                                        src={signatureSrc}
                                        alt="Principal signature preview"
                                        className="h-10 max-w-[140px] object-contain"
                                    />
                                    <button
                                        onClick={() => setSignatureSrc("")}
                                        className="text-xs text-red-600 hover:underline"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <button
                                    onClick={removeSignatureBackground}
                                    disabled={removingSignatureBg}
                                    className="w-full btn-secondary text-xs disabled:opacity-50"
                                    data-testid="remove-signature-bg-btn"
                                >
                                    {removingSignatureBg
                                        ? "Removing background..."
                                        : "Remove background"}
                                </button>
                                <button
                                    onClick={() =>
                                        addEl(DEFAULT_SIGNATURE(signatureSrc))
                                    }
                                    className="w-full btn-secondary text-xs"
                                    data-testid="add-signature-btn"
                                >
                                    Add signature to card
                                </button>
                            </div>
                        ) : null}
                    </div>

                    {/* Bulk export */}
                    {rows.length > 0 && (
                        <div className="p-4">
                            <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold mb-2">
                                Bulk Export · {rows.length} rows
                            </div>
                            <div className="space-y-2">
                                <button
                                    onClick={() => exportBulk("pdf")}
                                    disabled={exporting}
                                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-2.5 text-sm flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-500/30 disabled:opacity-50"
                                    data-testid="bulk-export-pdf"
                                >
                                    <FilePdf size={14} weight="fill" /> Export
                                    all → PDF
                                </button>
                                <button
                                    onClick={() => exportBulk("zip-png")}
                                    disabled={exporting}
                                    className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                                    data-testid="bulk-export-png"
                                >
                                    <FileImage size={14} /> Export all → PNGs
                                </button>
                                <div className="text-[10px] text-zinc-500 font-mono">
                                    Uses {rows.length} credits · 1 per card
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            {showTour && (
                <div className="fixed inset-0 z-[80] bg-zinc-950/60 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-lg border border-zinc-300 shadow-2xl">
                        <div className="p-5 border-b border-zinc-200">
                            <div className="text-xs font-mono uppercase tracking-widest text-blue-600">
                                Quick tour {tourStep + 1}/{TOUR_STEPS.length}
                            </div>
                            <h3 className="font-display font-black text-2xl tracking-tight mt-1">
                                {TOUR_STEPS[tourStep].title}
                            </h3>
                        </div>
                        <div className="p-5 text-sm text-zinc-700">
                            {TOUR_STEPS[tourStep].text}
                        </div>
                        <div className="p-5 pt-0 flex items-center justify-between gap-2">
                            <button
                                className="btn-secondary text-sm"
                                onClick={() => {
                                    setShowTour(false);
                                    localStorage.setItem(
                                        "idcardstudio_studio_tour_seen",
                                        "1",
                                    );
                                }}
                            >
                                Skip
                            </button>
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn-secondary text-sm"
                                    onClick={() =>
                                        setTourStep((s) => Math.max(0, s - 1))
                                    }
                                    disabled={tourStep === 0}
                                >
                                    Previous
                                </button>
                                <button
                                    className="btn-primary text-sm"
                                    onClick={() => {
                                        if (tourStep >= TOUR_STEPS.length - 1) {
                                            setShowTour(false);
                                            localStorage.setItem(
                                                "idcardstudio_studio_tour_seen",
                                                "1",
                                            );
                                            return;
                                        }
                                        setTourStep((s) => s + 1);
                                    }}
                                >
                                    {tourStep >= TOUR_STEPS.length - 1
                                        ? "Finish"
                                        : "Next"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ToolBtn({ title, icon: Icon, onClick, badge }) {
    return (
        <button
            title={title}
            onClick={onClick}
            className="relative w-10 h-10 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            data-testid={`tool-${title.toLowerCase().replace(/ /g, "-")}`}
        >
            <Icon size={18} />
            {badge && (
                <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-blue-500 text-white px-1 rounded">
                    {badge}
                </span>
            )}
        </button>
    );
}

function ElementProperties({ el, onChange, onDelete, columns }) {
    const set = (k) => (e) =>
        onChange({ ...el, [k]: typeof e === "object" ? e.target.value : e });
    const setNum = (k) => (e) =>
        onChange({ ...el, [k]: Number(e.target.value) });

    return (
        <div className="space-y-3 text-xs">
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-2">
                <div className="font-mono uppercase text-blue-900">
                    {el.kind} ·{" "}
                    <span className="text-zinc-800">{el.id.slice(0, 6)}</span>
                </div>
                <button
                    onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700 text-white p-1.5"
                    data-testid="el-delete-btn"
                    title="Delete (Del)"
                >
                    <Trash size={13} />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <LabelledInput label="X" value={el.x} onChange={setNum("x")} />
                <LabelledInput label="Y" value={el.y} onChange={setNum("y")} />
                <LabelledInput label="W" value={el.w} onChange={setNum("w")} />
                <LabelledInput label="H" value={el.h} onChange={setNum("h")} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <LabelledInput
                    label="Z-index"
                    value={el.z || 1}
                    onChange={setNum("z")}
                />
                <LabelledInput
                    label="Rotation"
                    value={el.rotation || 0}
                    onChange={setNum("rotation")}
                />
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min="-180"
                    max="180"
                    step="1"
                    value={el.rotation || 0}
                    onChange={setNum("rotation")}
                    className="flex-1"
                    data-testid="el-rotate-slider"
                />
                <div className="flex gap-1">
                    {[-90, 0, 90, 180].map((deg) => (
                        <button
                            key={deg}
                            type="button"
                            onClick={() => onChange({ ...el, rotation: deg })}
                            className="px-1.5 py-0.5 text-[10px] font-mono border border-zinc-300 hover:bg-violet-50 hover:border-violet-300"
                        >
                            {deg}°
                        </button>
                    ))}
                </div>
            </div>

            {el.kind === "text" && (
                <>
                    <div>
                        <label className="label-swiss">Text</label>
                        <textarea
                            className="input-swiss"
                            rows={2}
                            value={el.text}
                            onChange={set("text")}
                        />
                        <div className="text-[10px] text-zinc-500 mt-1">
                            Use <code>{"{{ColumnName}}"}</code> to bind Excel
                            data
                        </div>
                    </div>
                    <div>
                        <label className="label-swiss">Bind to column</label>
                        <select
                            className="input-swiss"
                            value={el.bind || ""}
                            onChange={set("bind")}
                        >
                            <option value="">— None —</option>
                            {columns.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <LabelledInput
                            label="Font size"
                            value={el.fontSize}
                            onChange={setNum("fontSize")}
                        />
                        <div>
                            <label className="label-swiss">Weight</label>
                            <select
                                className="input-swiss"
                                value={el.fontWeight}
                                onChange={set("fontWeight")}
                            >
                                {[
                                    "300",
                                    "400",
                                    "500",
                                    "600",
                                    "700",
                                    "800",
                                    "900",
                                ].map((w) => (
                                    <option key={w}>{w}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-swiss">Font family</label>
                        <select
                            className="input-swiss"
                            value={el.fontFamily}
                            onChange={set("fontFamily")}
                            style={{ fontFamily: el.fontFamily }}
                        >
                            <optgroup label="Sans Serif">
                                <option
                                    value="Inter, sans-serif"
                                    style={{ fontFamily: "Inter, sans-serif" }}
                                >
                                    Inter
                                </option>
                                <option
                                    value="Roboto, sans-serif"
                                    style={{ fontFamily: "Roboto, sans-serif" }}
                                >
                                    Roboto
                                </option>
                                <option
                                    value="'Open Sans', sans-serif"
                                    style={{
                                        fontFamily: "'Open Sans', sans-serif",
                                    }}
                                >
                                    Open Sans
                                </option>
                                <option
                                    value="Poppins, sans-serif"
                                    style={{
                                        fontFamily: "Poppins, sans-serif",
                                    }}
                                >
                                    Poppins
                                </option>
                                <option
                                    value="Montserrat, sans-serif"
                                    style={{
                                        fontFamily: "Montserrat, sans-serif",
                                    }}
                                >
                                    Montserrat
                                </option>
                                <option
                                    value="Lato, sans-serif"
                                    style={{ fontFamily: "Lato, sans-serif" }}
                                >
                                    Lato
                                </option>
                                <option
                                    value="Nunito, sans-serif"
                                    style={{ fontFamily: "Nunito, sans-serif" }}
                                >
                                    Nunito
                                </option>
                                <option
                                    value="Raleway, sans-serif"
                                    style={{
                                        fontFamily: "Raleway, sans-serif",
                                    }}
                                >
                                    Raleway
                                </option>
                                <option
                                    value="Oswald, sans-serif"
                                    style={{ fontFamily: "Oswald, sans-serif" }}
                                >
                                    Oswald
                                </option>
                                <option
                                    value="'Bebas Neue', sans-serif"
                                    style={{
                                        fontFamily: "'Bebas Neue', sans-serif",
                                    }}
                                >
                                    Bebas Neue
                                </option>
                                <option value="Satoshi, sans-serif">
                                    Satoshi
                                </option>
                                <option value="'Cabinet Grotesk', sans-serif">
                                    Cabinet Grotesk
                                </option>
                                <option value="Arial, sans-serif">Arial</option>
                                <option value="Helvetica, sans-serif">
                                    Helvetica
                                </option>
                                <option value="Verdana, sans-serif">
                                    Verdana
                                </option>
                                <option value="Tahoma, sans-serif">
                                    Tahoma
                                </option>
                                <option value="'Trebuchet MS', sans-serif">
                                    Trebuchet MS
                                </option>
                            </optgroup>
                            <optgroup label="Serif">
                                <option
                                    value="'Playfair Display', serif"
                                    style={{
                                        fontFamily: "'Playfair Display', serif",
                                    }}
                                >
                                    Playfair Display
                                </option>
                                <option
                                    value="Merriweather, serif"
                                    style={{
                                        fontFamily: "Merriweather, serif",
                                    }}
                                >
                                    Merriweather
                                </option>
                                <option value="Georgia, serif">Georgia</option>
                                <option value="'Times New Roman', serif">
                                    Times New Roman
                                </option>
                            </optgroup>
                            <optgroup label="Monospace">
                                <option value="'JetBrains Mono', monospace">
                                    JetBrains Mono
                                </option>
                                <option value="'Courier New', monospace">
                                    Courier New
                                </option>
                            </optgroup>
                            <optgroup label="Devanagari (हिंदी)">
                                <option
                                    value="'Noto Sans Devanagari', sans-serif"
                                    style={{
                                        fontFamily:
                                            "'Noto Sans Devanagari', sans-serif",
                                    }}
                                >
                                    Noto Sans Devanagari
                                </option>
                                <option
                                    value="Hind, sans-serif"
                                    style={{ fontFamily: "Hind, sans-serif" }}
                                >
                                    Hind
                                </option>
                                <option
                                    value="Mukta, sans-serif"
                                    style={{ fontFamily: "Mukta, sans-serif" }}
                                >
                                    Mukta
                                </option>
                            </optgroup>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <ColorInput
                            label="Color"
                            value={el.color}
                            onChange={set("color")}
                        />
                        <div>
                            <label className="label-swiss">Align</label>
                            <select
                                className="input-swiss"
                                value={el.align}
                                onChange={set("align")}
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                    </div>
                    <ColorInput
                        label="Background"
                        value={el.bg === "transparent" ? "#ffffff" : el.bg}
                        onChange={set("bg")}
                    />
                </>
            )}

            {el.kind === "photo" && (
                <>
                    <div>
                        <label className="label-swiss">
                            Bind to column (student photo filename)
                        </label>
                        <select
                            className="input-swiss"
                            value={el.bind || ""}
                            onChange={set("bind")}
                        >
                            <option value="">— None —</option>
                            {columns.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                        <div className="text-[10px] text-zinc-500 mt-1">
                            Tip: match this to the column holding filenames
                            (e.g. <code>Photo</code>).
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <LabelledInput
                            label="Border"
                            value={el.border}
                            onChange={setNum("border")}
                        />
                        <LabelledInput
                            label="Radius"
                            value={el.radius}
                            onChange={setNum("radius")}
                        />
                    </div>
                    <ColorInput
                        label="Border color"
                        value={el.borderColor}
                        onChange={set("borderColor")}
                    />
                </>
            )}

            {el.kind === "rect" && (
                <>
                    <ColorInput
                        label="Fill"
                        value={el.bg}
                        onChange={set("bg")}
                    />
                    <LabelledInput
                        label="Radius"
                        value={el.radius}
                        onChange={setNum("radius")}
                    />
                </>
            )}

            {el.kind === "qr" && (
                <>
                    <div>
                        <label className="label-swiss">Value</label>
                        <input
                            className="input-swiss"
                            value={el.value}
                            onChange={set("value")}
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Bind to column</label>
                        <select
                            className="input-swiss"
                            value={el.bind || ""}
                            onChange={(e) =>
                                onChange({
                                    ...el,
                                    bind: e.target.value,
                                    value: e.target.value
                                        ? `{{${e.target.value}}}`
                                        : el.value,
                                })
                            }
                        >
                            <option value="">— None —</option>
                            {columns.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <ColorInput
                            label="Color"
                            value={el.color}
                            onChange={set("color")}
                        />
                        <ColorInput
                            label="Background"
                            value={el.bg}
                            onChange={set("bg")}
                        />
                    </div>
                </>
            )}

            {el.kind === "barcode" && (
                <>
                    <div>
                        <label className="label-swiss">Value</label>
                        <input
                            className="input-swiss"
                            value={el.value}
                            onChange={set("value")}
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Bind to column</label>
                        <select
                            className="input-swiss"
                            value={el.bind || ""}
                            onChange={(e) =>
                                onChange({
                                    ...el,
                                    bind: e.target.value,
                                    value: e.target.value
                                        ? `{{${e.target.value}}}`
                                        : el.value,
                                })
                            }
                        >
                            <option value="">— None —</option>
                            {columns.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="label-swiss">Format</label>
                        <select
                            className="input-swiss"
                            value={el.format}
                            onChange={set("format")}
                        >
                            {[
                                "CODE128",
                                "CODE39",
                                "EAN13",
                                "EAN8",
                                "UPC",
                                "ITF14",
                                "MSI",
                                "pharmacode",
                                "codabar",
                            ].map((f) => (
                                <option key={f}>{f}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <ColorInput
                            label="Color"
                            value={el.color}
                            onChange={set("color")}
                        />
                        <ColorInput
                            label="Background"
                            value={el.bg}
                            onChange={set("bg")}
                        />
                    </div>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={el.showText}
                            onChange={(e) =>
                                onChange({ ...el, showText: e.target.checked })
                            }
                        />
                        Show text under bars
                    </label>
                </>
            )}

            {el.kind === "image" && (
                <LabelledInput
                    label="Radius"
                    value={el.radius}
                    onChange={setNum("radius")}
                />
            )}

            {el.kind === "signature" && (
                <>
                    <LabelledInput
                        label="Radius"
                        value={el.radius}
                        onChange={setNum("radius")}
                    />
                    <div>
                        <label className="label-swiss">Transparency</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={
                                typeof el.opacity === "number" ? el.opacity : 1
                            }
                            onChange={(e) =>
                                onChange({
                                    ...el,
                                    opacity: Number(e.target.value),
                                })
                            }
                            className="w-full"
                        />
                        <div className="text-[10px] text-zinc-500 mt-1">
                            {Math.round(
                                (typeof el.opacity === "number"
                                    ? el.opacity
                                    : 1) * 100,
                            )}
                            % visible
                        </div>
                    </div>
                    <div>
                        <label className="label-swiss">Blend mode</label>
                        <select
                            className="input-swiss"
                            value={el.blend || "normal"}
                            onChange={(e) =>
                                onChange({ ...el, blend: e.target.value })
                            }
                        >
                            <option value="normal">Normal</option>
                            <option value="multiply">
                                Multiply (hide white-ish background)
                            </option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );
}

function LabelledInput({ label, value, onChange }) {
    return (
        <div>
            <label className="label-swiss">{label}</label>
            <input
                className="input-swiss font-mono"
                value={value}
                onChange={onChange}
            />
        </div>
    );
}
function ColorInput({ label, value, onChange }) {
    return (
        <div>
            <label className="label-swiss">{label}</label>
            <div className="flex gap-1">
                <input
                    type="color"
                    className="w-10 h-9 border border-zinc-300 p-0 cursor-pointer"
                    value={value || "#000000"}
                    onChange={onChange}
                />
                <input
                    className="input-swiss font-mono"
                    value={value}
                    onChange={onChange}
                />
            </div>
        </div>
    );
}
